import React, { Fragment, useEffect, useRef, useState } from 'react';
import isEqual from 'lodash.isequal';
import Tippy from '@tippy.js/react'; // https://github.com/tvkhoa/react-tippy/issues/107
import cn from 'classnames';
import PropTypes from 'prop-types';

import styles from './AddToWatchlistButton.css';

import Modal from '../Modal/Modal';
import WatchlistSelection from '../WatchlistSelection/WatchlistSelection';
import {
    useWatchlistDispatch,
    useWatchlistState
} from '../../Hooks/useWatchlist';
import { isMobileWidth } from '../../theme/layoutUtilities';
import greenCheck from '../../images/green-checkmark.svg';
import minusCollapse from '../../images/icon-minus-white.svg';
import plusExpand from '../../images/icon-plus-white.svg';
import {
    registerResizeHandler,
    unregisterResizeHandler
} from '../../utils/resize-handler';
import {
    ADD_WATCHLIST_FUND,
    REMOVE_SELECTED_WATCHLIST_FUNDS,
    WATCHLIST_SELECTION
} from '../../utils/watchlist-settings';

const AddtoWatchlistButton = ({
    children,
    funds,
    ticker,
    selectionPlacement = 'bottom',
    selectorArrow = false,
    loggedIn,
    loud,
    removalOnly
}) => {
    const {
        selectedId: watchlistUuid,
        watchlists,
        watchlistFunds
    } = useWatchlistState();

    const dispatchWatchlist = useWatchlistDispatch();
    const tippyInstance = useRef();
    const [mobileWidth, setMobileWidth] = useState(isMobileWidth());
    const toggleMobileWidth = () => setMobileWidth(isMobileWidth());

    useEffect(() => {
        registerResizeHandler(toggleMobileWidth);
        return () => unregisterResizeHandler(toggleMobileWidth);
    }, []);

    const tippyEnabled = loggedIn && !mobileWidth && !removalOnly;
    const [modalToDisplay, dispatchModal] = useState(false);

    // compile one list of all tickers from every Watchlist
    const compileTickers = () => {
        if (!(watchlists && watchlists.length)) return watchlistFunds;
        const allTickers = watchlists.reduce((accumulator, watchlist) => {
            if (watchlist.items && watchlist.items.length) {
                const tickers = watchlist.items.map((item) => item.ticker);
                return accumulator.concat(tickers);
            }
            return accumulator;
        }, []);
        return allTickers;
    };

    const allWatchlistFunds = compileTickers();

    // display add button as remove if watchlist funds
    // and compare funds match
    const searchWatchlists = () => {
        // if removing, need only to search currently selected watchlist
        const tickersToCheck = removalOnly ? watchlistFunds : allWatchlistFunds;
        if (!ticker && !(funds && funds.length)) return false;
        // checking a single fund
        if (funds && funds.length === 1)
            return tickersToCheck.includes(ticker || funds[0]);
        // checking mutliple funds
        if (Array.isArray(funds))
            return (
                isEqual(tickersToCheck, funds) ||
                funds.every((f) => tickersToCheck.includes(f))
            );

        return false;
    };

    const isAddedToWatchlist = searchWatchlists();

    return (
        <Fragment>
            <Tippy
                content={
                    <div
                        className="selection-container"
                        onClick={() =>
                            tippyInstance.current &&
                            tippyInstance.current.hide()
                        }
                        onKeyPress={() =>
                            tippyInstance.current &&
                            tippyInstance.current.hide()
                        }
                        role="button"
                        tabIndex="0"
                    >
                        <WatchlistSelection
                            dispatchWatchlist={dispatchWatchlist}
                            watchlists={watchlists}
                            watchlistUuid={watchlistUuid}
                            funds={ticker || funds}
                            isAddFeature={!isAddedToWatchlist}
                            isRemoveFeature={isAddedToWatchlist}
                        />
                    </div>
                }
                onCreate={(instance) => {
                    tippyInstance.current = instance;
                }}
                hideOnClick
                interactive
                trigger="click"
                placement={selectionPlacement}
                enabled={tippyEnabled}
                arrow={selectorArrow}
            >
                {isAddedToWatchlist ? (
                    <button
                        className={cn('add-to-watchlist-btn is-added', {
                            loud,
                            'watchlist-remove': removalOnly
                        })}
                        onClick={() => {
                            if (!loggedIn) {
                                dispatchWatchlist({
                                    type: REMOVE_SELECTED_WATCHLIST_FUNDS,
                                    selectedFunds: ticker || funds
                                });
                            } else if (removalOnly) {
                                dispatchWatchlist({
                                    type: REMOVE_SELECTED_WATCHLIST_FUNDS,
                                    selectedFunds: ticker || funds,
                                    selectedWatchlist: watchlists.find(
                                        (wl) =>
                                            wl.watchlistUuid === watchlistUuid
                                    )
                                });
                            } else if (mobileWidth) {
                                dispatchModal(WATCHLIST_SELECTION);
                            }
                        }}
                        type="button"
                    >
                        {removalOnly ? (
                            <span>
                                {mobileWidth && (
                                    <img
                                        className="added-icon-img"
                                        src={minusCollapse}
                                        alt="Remove"
                                    />
                                )}
                                {mobileWidth
                                    ? 'Watchlist'
                                    : 'Remove From Watchlist'}
                            </span>
                        ) : (
                            <span>
                                <img
                                    className="added-icon-img"
                                    src={
                                        mobileWidth && !loud
                                            ? minusCollapse
                                            : greenCheck
                                    }
                                    alt="Added to Watchlist"
                                />
                                {mobileWidth && !loud
                                    ? 'Watchlist'
                                    : 'Added To Watchlist'}
                            </span>
                        )}
                    </button>
                ) : (
                    <button
                        className={cn('add-to-watchlist-btn', {
                            loud
                        })}
                        onClick={() => {
                            if (!loggedIn) {
                                dispatchWatchlist({
                                    type: ADD_WATCHLIST_FUND,
                                    ticker: ticker || funds
                                });
                            } else if (mobileWidth)
                                dispatchModal(WATCHLIST_SELECTION);
                        }}
                        type="button"
                    >
                        {children || (
                            <React.Fragment>
                                <span className={cn('add-to-text', { loud })}>
                                    {mobileWidth &&
                                        !loud && (
                                            <img
                                                className="added-icon-img"
                                                src={plusExpand}
                                                alt="Add to Watchlist"
                                            />
                                        )}
                                    {mobileWidth && !loud
                                        ? 'Watchlist'
                                        : 'Add To Watchlist'}
                                </span>
                            </React.Fragment>
                        )}
                    </button>
                )}
            </Tippy>
            <Modal
                dispatchModal={dispatchModal}
                modalToDisplay={modalToDisplay}
                dispatchWatchlist={dispatchWatchlist}
                watchlists={watchlists}
                watchlistUuid={watchlistUuid}
                funds={ticker || funds}
                isAddFeature={!isAddedToWatchlist}
                isRemoveFeature={isAddedToWatchlist}
            />
            <style jsx>{styles}</style>
        </Fragment>
    );
};

AddtoWatchlistButton.defaultProps = {
    loud: false
};

AddtoWatchlistButton.propTypes = {
    loud: PropTypes.bool
};

export default AddtoWatchlistButton;
